import React, { useState, useEffect, useRef } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Avatar,
  useMediaQuery,
  CssBaseline,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Paper,
  Tab,
  Tabs,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  EventNote as EventIcon,
  Description as DescriptionIcon,
  AccountBox as AccountBoxIcon,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  People as PeopleIcon,
  Dashboard as DashboardIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Draw as DrawIcon,
  Upload as UploadIcon,
  Clear as ClearIcon,
  KeyboardTab as KeyboardTabIcon,
  Download as DownloadIcon,
  CalendarMonth as CalendarMonthIcon
} from '@mui/icons-material';

import Consultas from './Consultas';
import Pacientes from './Pacientes';
import Documentos from './Documentos';
import AgendaPsicologo from './AgendaPsicologo';

const drawerWidth = 280;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      padding: theme.spacing(2),
    },
  }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

function Dashboard() {
  const [selectedTab, setSelectedTab] = useState('dashboard');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [psicologoId, setPsicologoId] = useState('');
  const [nomePsicologo, setNomePsicologo] = useState('');
  const [iniciais, setIniciais] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [assinatura, setAssinatura] = useState('');
  const [assinaturaImg, setAssinaturaImg] = useState('');
  const [assinaturaTab, setAssinaturaTab] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const [perfilData, setPerfilData] = useState({
    email: '',
    telefone: '',
    especialidade: '',
    valorConsulta: '',
    formacao: [
      'Graduação em Psicologia - Universidade de São Paulo (USP)',
      '',
      'Mestrado em Psicologia Clínica - Universidade Federal de São Paulo (UNIFESP)'
    ],
    abordagens: [
      '',
      'Atendimento de adultos e adolescentes',
      'Tratamento de ansiedade, depressão e transtornos de humor',
      'Desenvolvimento pessoal e autoconhecimento',
      ''
    ]
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // Verificar se o usuário é um psicólogo
    const tipoUsuario = localStorage.getItem('userType');
    
    if (tipoUsuario !== 'psicologo') {
      // Redirecionar para a página de login se não for psicólogo
      window.location.href = '/login';
      return;
    }
    
    // Obter o ID do psicólogo do localStorage
    const id = localStorage.getItem('userId');
    if (id) {
      setPsicologoId(id);
      
      // Definir o nome do psicólogo com base no ID
      if (id === '1') {
        setNomePsicologo('Dra. Rafaela Mendes Luz');
        setIniciais('RL');
        setPerfilData({
          email: 'rafaela.mendes@exemplo.com',
          telefone: '(11) 98765-4321',
          especialidade: 'Terapia Cognitivo-Comportamental',
          valorConsulta: '200,00',
          formacao: [
            'Graduação em Psicologia - Universidade de São Paulo (USP)',
            'Especialização em Terapia Cognitivo-Comportamental - PUC-SP',
            'Mestrado em Psicologia Clínica - Universidade Federal de São Paulo (UNIFESP)'
          ],
          abordagens: [
            'Terapia Cognitivo-Comportamental (TCC)',
            'Atendimento de adultos e adolescentes',
            'Tratamento de ansiedade, depressão e transtornos de humor',
            'Desenvolvimento pessoal e autoconhecimento',
            'Psicologia positiva e bem-estar'
          ]
        });
        setAssinatura('Dra. Rafaela Mendes Luz\nCRP 06/12345\nPsicóloga Clínica');
      } else if (id === '2') {
        setNomePsicologo('Dr. João Silva');
        setIniciais('JS');
        setPerfilData({
          email: 'joao.silva@exemplo.com',
          telefone: '(11) 91234-5678',
          especialidade: 'Psicanálise',
          valorConsulta: '180,00',
          formacao: [
            'Graduação em Psicologia - Universidade de São Paulo (USP)',
            'Especialização em Psicanálise - Instituto de Psicanálise de SP',
            'Mestrado em Psicologia Clínica - Universidade Federal de São Paulo (UNIFESP)'
          ],
          abordagens: [
            'Psicanálise Contemporânea',
            'Atendimento de adultos e adolescentes',
            'Tratamento de ansiedade, depressão e transtornos de humor',
            'Desenvolvimento pessoal e autoconhecimento',
            'Relações interpessoais e dinâmicas familiares'
          ]
        });
        setAssinatura('Dr. João Silva\nCRP 06/54321\nPsicólogo Clínico');
      }
    }
    
    // Em telas grandes, o drawer fica aberto por padrão
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  // Configurar o canvas para desenho quando o componente montar ou quando mudar para o modo de edição
  useEffect(() => {
    if (editMode && assinaturaTab === 1 && canvasRef.current) {
      const canvas = canvasRef.current;
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      
      const ctx = canvas.getContext("2d");
      ctx.scale(1, 1);
      ctx.lineCap = "round";
      ctx.strokeStyle = "black";
      ctx.lineWidth = 2;
      ctxRef.current = ctx;
    }
  }, [editMode, assinaturaTab]);

  // Carregar imagem de assinatura existente
  useEffect(() => {
    if (psicologoId) {
      // Simular o carregamento da imagem de assinatura do banco de dados
      if (psicologoId === '1') {
        // Se existir uma assinatura em imagem para este psicólogo
        setAssinaturaImg('');
      } else if (psicologoId === '2') {
        setAssinaturaImg('');
      }
    }
  }, [psicologoId]);

  // Funções para desenho da assinatura
  const startDrawing = ({ nativeEvent }) => {
    const { offsetX, offsetY } = nativeEvent;
    ctxRef.current.beginPath();
    ctxRef.current.moveTo(offsetX, offsetY);
    setIsDrawing(true);
  };

  const finishDrawing = () => {
    ctxRef.current.closePath();
    setIsDrawing(false);
    // Salvar o desenho como imagem
    if (canvasRef.current) {
      const imgUrl = canvasRef.current.toDataURL("image/png");
      setAssinaturaImg(imgUrl);
    }
  };

  const draw = ({ nativeEvent }) => {
    if (!isDrawing) return;
    const { offsetX, offsetY } = nativeEvent;
    ctxRef.current.lineTo(offsetX, offsetY);
    ctxRef.current.stroke();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setAssinaturaImg('');
  };

  const handleAssinaturaTabChange = (event, newValue) => {
    setAssinaturaTab(newValue);
  };

  const handleAssinaturaUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.match('image.*')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setAssinaturaImg(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const downloadAssinatura = () => {
    // Criar um link para download
    const link = document.createElement('a');
    link.download = 'minha-assinatura.png';
    link.href = assinaturaImg;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userType');
    localStorage.removeItem('userId');
    localStorage.removeItem('email');
    window.location.href = '/login';
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <DashboardIcon />
    },
    {
      id: 'agenda',
      label: 'Agenda',
      icon: <CalendarMonthIcon />
    },
    {
      id: 'consultas',
      label: 'Consultas',
      icon: <EventIcon />
    },
    {
      id: 'pacientes',
      label: 'Pacientes',
      icon: <PeopleIcon />
    },
    {
      id: 'documentos',
      label: 'Documentos',
      icon: <DescriptionIcon />
    },
    {
      id: 'perfil',
      label: 'Perfil',
      icon: <AccountBoxIcon />
    }
  ];

  const handleInputChange = (field, value) => {
    setPerfilData({
      ...perfilData,
      [field]: value
    });
  };

  const handleFormacaoChange = (index, value) => {
    const newFormacao = [...perfilData.formacao];
    newFormacao[index] = value;
    setPerfilData({
      ...perfilData,
      formacao: newFormacao
    });
  };

  const handleAbordagemChange = (index, value) => {
    const newAbordagens = [...perfilData.abordagens];
    newAbordagens[index] = value;
    setPerfilData({
      ...perfilData,
      abordagens: newAbordagens
    });
  };

  const renderContent = () => {
    switch (selectedTab) {
      case 'dashboard':
        return (
          <Box>
            <Typography variant="h4" gutterBottom>
              Dashboard
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={3}>
                <Card sx={{ 
                  bgcolor: 'rgba(74, 138, 132, 0.1)', 
                  borderLeft: '4px solid #4a8a84',
                  height: '100%'
                }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Agenda de Hoje
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#4a8a84', fontWeight: 'bold' }}>
                      3
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Consultas agendadas
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6} lg={3}>
                <Card sx={{ 
                  bgcolor: 'rgba(94, 114, 228, 0.1)', 
                  borderLeft: '4px solid #5e72e4',
                  height: '100%'
                }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Pacientes Ativos
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#5e72e4', fontWeight: 'bold' }}>
                      28
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total de pacientes
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6} lg={3}>
                <Card sx={{ 
                  bgcolor: 'rgba(251, 99, 64, 0.1)', 
                  borderLeft: '4px solid #fb6340',
                  height: '100%'
                }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Pendentes
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#fb6340', fontWeight: 'bold' }}>
                      2
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Documentos para revisar
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6} lg={3}>
                <Card sx={{ 
                  bgcolor: 'rgba(17, 205, 239, 0.1)', 
                  borderLeft: '4px solid #11cdef',
                  height: '100%'
                }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Aniversariantes
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#11cdef', fontWeight: 'bold' }}>
                      1
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Neste mês
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" gutterBottom>
                Próximas Consultas
              </Typography>
              <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, pb: 2, borderBottom: '1px solid #eee' }}>
                  <Avatar sx={{ bgcolor: '#4a8a84', mr: 2 }}>AM</Avatar>
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      Ana Maria Costa
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Hoje, 14:00 - 15:00 • Online
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, pb: 2, borderBottom: '1px solid #eee' }}>
                  <Avatar sx={{ bgcolor: '#5e72e4', mr: 2 }}>CR</Avatar>
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      Carlos Roberto Dias
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Hoje, 16:30 - 17:30 • Presencial
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: '#fb6340', mr: 2 }}>MP</Avatar>
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      Maria Paula Santos
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Hoje, 19:00 - 20:00 • Online
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
        );
      case 'agenda':
        return <AgendaPsicologo />;
      case 'consultas':
        return <Consultas psicologoId={psicologoId} />;
      case 'pacientes':
        return <Pacientes psicologoId={psicologoId} />;
      case 'documentos':
        return <Documentos psicologoId={psicologoId} />;
      case 'perfil':
        return (
          <Box>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3
            }}>
              <Typography variant="h4">
                Perfil Profissional
              </Typography>
              {!editMode ? (
                <Button 
                  variant="outlined" 
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode(true)}
                  sx={{ color: '#4a8a84', borderColor: '#4a8a84' }}
                >
                  Editar Perfil
                </Button>
              ) : (
                <Box>
                  <Button 
                    variant="outlined" 
                    startIcon={<CancelIcon />}
                    onClick={() => setEditMode(false)}
                    sx={{ mr: 1 }}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    variant="contained" 
                    startIcon={<SaveIcon />}
                    onClick={() => setEditMode(false)}
                    sx={{ bgcolor: '#4a8a84', '&:hover': { bgcolor: '#3a7a74' } }}
                  >
                    Salvar
                  </Button>
                </Box>
              )}
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' }, 
              gap: 4, 
              mb: 4 
            }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                p: 3,
                bgcolor: 'rgba(74, 138, 132, 0.05)',
                borderRadius: 2,
                minWidth: 250
              }}>
                <Avatar sx={{ 
                  width: 120, 
                  height: 120, 
                  bgcolor: '#4a8a84',
                  fontSize: 40,
                  mb: 2
                }}>
                  {iniciais}
                </Avatar>
                <Typography variant="h5" gutterBottom>
                  {nomePsicologo}
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  Psicólogo(a) Clínico(a)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CRP: {psicologoId === '1' ? '06/12345' : '06/54321'}
                </Typography>
                {editMode && (
                  <Button 
                    variant="outlined" 
                    size="small"
                    sx={{ mt: 2, color: '#4a8a84', borderColor: '#4a8a84' }}
                  >
                    Alterar Foto
                  </Button>
                )}
              </Box>
              
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Informações Pessoais
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      E-mail
                    </Typography>
                    {editMode ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={perfilData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        sx={{ mt: 1 }}
                      />
                    ) : (
                      <Typography variant="body1" gutterBottom>
                        {perfilData.email}
                      </Typography>
                    )}
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Telefone
                    </Typography>
                    {editMode ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={perfilData.telefone}
                        onChange={(e) => handleInputChange('telefone', e.target.value)}
                        sx={{ mt: 1 }}
                      />
                    ) : (
                      <Typography variant="body1" gutterBottom>
                        {perfilData.telefone}
                      </Typography>
                    )}
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Especialidade
                    </Typography>
                    {editMode ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={perfilData.especialidade}
                        onChange={(e) => handleInputChange('especialidade', e.target.value)}
                        sx={{ mt: 1 }}
                      />
                    ) : (
                      <Typography variant="body1" gutterBottom>
                        {perfilData.especialidade}
                      </Typography>
                    )}
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Valor da Consulta
                    </Typography>
                    {editMode ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={perfilData.valorConsulta}
                        onChange={(e) => handleInputChange('valorConsulta', e.target.value)}
                        InputProps={{ startAdornment: 'R$ ' }}
                        sx={{ mt: 1 }}
                      />
                    ) : (
                      <Typography variant="body1" gutterBottom>
                        R$ {perfilData.valorConsulta}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
            
            <Typography variant="h6" gutterBottom>
              Assinatura Digital
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 4 }}>
              {editMode ? (
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Tabs 
                    value={assinaturaTab} 
                    onChange={handleAssinaturaTabChange}
                    sx={{ 
                      mb: 2,
                      '& .MuiTab-root': { 
                        textTransform: 'none',
                        minWidth: 'auto',
                        px: 2
                      },
                      '& .Mui-selected': { 
                        color: '#4a8a84',
                        fontWeight: 'bold'
                      },
                      '& .MuiTabs-indicator': { 
                        backgroundColor: '#4a8a84' 
                      } 
                    }}
                  >
                    <Tab label="Texto" icon={<KeyboardTabIcon />} iconPosition="start" />
                    <Tab label="Desenhar" icon={<DrawIcon />} iconPosition="start" />
                    <Tab label="Enviar Imagem" icon={<UploadIcon />} iconPosition="start" />
                  </Tabs>
                  
                  {assinaturaTab === 0 && (
                    <Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={assinatura}
                        onChange={(e) => setAssinatura(e.target.value)}
                        placeholder="Digite sua assinatura profissional aqui..."
                        helperText="Esta assinatura será usada em documentos como atestados, declarações e relatórios."
                      />
                    </Box>
                  )}
                  
                  {assinaturaTab === 1 && (
                    <Box>
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          p: 1, 
                          border: '2px dashed #ccc', 
                          borderRadius: 2, 
                          bgcolor: '#f9f9f9',
                          height: 200,
                          mb: 2
                        }}
                      >
                        <canvas
                          ref={canvasRef}
                          onMouseDown={startDrawing}
                          onMouseUp={finishDrawing}
                          onMouseMove={draw}
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            cursor: 'crosshair',
                            backgroundColor: '#fff',
                            borderRadius: 8
                          }}
                        />
                      </Paper>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          Desenhe sua assinatura no espaço acima
                        </Typography>
                        <Tooltip title="Limpar">
                          <IconButton onClick={clearCanvas} size="small">
                            <ClearIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  )}
                  
                  {assinaturaTab === 2 && (
                    <Box>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="assinatura-upload"
                        type="file"
                        onChange={handleAssinaturaUpload}
                      />
                      <label htmlFor="assinatura-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<UploadIcon />}
                          sx={{ mb: 2 }}
                        >
                          Selecionar Imagem
                        </Button>
                      </label>
                      
                      {assinaturaImg && (
                        <Box>
                          <Paper 
                            elevation={0} 
                            sx={{ 
                              p: 2, 
                              border: '1px solid #eee', 
                              borderRadius: 2, 
                              bgcolor: '#fff',
                              mb: 2,
                              textAlign: 'center'
                            }}
                          >
                            <img 
                              src={assinaturaImg} 
                              alt="Assinatura" 
                              style={{ 
                                maxWidth: '100%', 
                                maxHeight: 150,
                                display: 'inline-block'
                              }} 
                            />
                          </Paper>
                        </Box>
                      )}
                    </Box>
                  )}
                </Card>
              ) : (
                <Box>
                  {assinaturaImg ? (
                    <Box 
                      sx={{ 
                        p: 3, 
                        border: '1px solid #eee', 
                        borderRadius: 2, 
                        bgcolor: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                      }}
                    >
                      <img 
                        src={assinaturaImg} 
                        alt="Assinatura" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: 150,
                          marginBottom: 8
                        }} 
                      />
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={downloadAssinatura}
                        sx={{ mt: 1 }}
                      >
                        Baixar Assinatura
                      </Button>
                    </Box>
                  ) : assinatura ? (
                    <Box sx={{ 
                      p: 3, 
                      border: '1px solid #eee', 
                      borderRadius: 2, 
                      bgcolor: '#fff'
                    }}>
                      <Box sx={{ 
                        whiteSpace: 'pre-line',
                        fontFamily: 'cursive',
                        fontSize: '1.2rem',
                        color: '#333',
                        p: 2
                      }}>
                        {assinatura}
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ 
                      p: 3, 
                      border: '1px dashed #ccc', 
                      borderRadius: 2, 
                      bgcolor: '#f9f9f9',
                      textAlign: 'center',
                      color: '#666'
                    }}>
                      <Typography>Nenhuma assinatura cadastrada</Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Clique em "Editar Perfil" para adicionar sua assinatura
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            
            <Typography variant="h6" gutterBottom>
              Formação Acadêmica
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ pl: 2, mb: 4 }}>
              {editMode ? (
                perfilData.formacao.map((item, index) => (
                  <TextField
                    key={index}
                    fullWidth
                    size="small"
                    value={item}
                    onChange={(e) => handleFormacaoChange(index, e.target.value)}
                    sx={{ mb: 1 }}
                    placeholder={`Formação ${index + 1}`}
                  />
                ))
              ) : (
                perfilData.formacao.map((item, index) => (
                  <Typography key={index} variant="body1" gutterBottom>
                    • {item}
                  </Typography>
                ))
              )}
            </Box>
            
            <Typography variant="h6" gutterBottom>
              Abordagens e Áreas de Atuação
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ pl: 2 }}>
              {editMode ? (
                perfilData.abordagens.map((item, index) => (
                  <TextField
                    key={index}
                    fullWidth
                    size="small"
                    value={item}
                    onChange={(e) => handleAbordagemChange(index, e.target.value)}
                    sx={{ mb: 1 }}
                    placeholder={`Abordagem ou área ${index + 1}`}
                  />
                ))
              ) : (
                perfilData.abordagens.map((item, index) => (
                  <Typography key={index} variant="body1" gutterBottom>
                    • {item}
                  </Typography>
                ))
              )}
            </Box>
          </Box>
        );
      default:
        return <Consultas psicologoId={psicologoId} />;
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: '#4a8a84'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Painel do Psicólogo
          </Typography>
          <IconButton color="inherit">
            <Badge badgeContent={4} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
            <Avatar sx={{ bgcolor: '#3a7a74', marginRight: 1 }}>
              {iniciais}
            </Avatar>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {nomePsicologo}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={drawerOpen}
        onClose={isMobile ? handleDrawerToggle : undefined}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        ModalProps={{
          keepMounted: true // Melhor desempenho em dispositivos móveis
        }}
      >
        <DrawerHeader>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            flexGrow: 1, 
            ml: 2,
            gap: 2
          }}>
            <Avatar sx={{ bgcolor: '#4a8a84' }}>{iniciais}</Avatar>
            <Typography variant="h6" color="text.primary">
              {nomePsicologo}
            </Typography>
          </Box>
          <IconButton onClick={handleDrawerToggle}>
            <ChevronLeftIcon />
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.id} disablePadding>
              <ListItemButton
                selected={selectedTab === item.id}
                onClick={() => handleTabChange(item.id)}
                sx={{
                  py: 1.5,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(74, 138, 132, 0.1)',
                    borderLeft: '4px solid #4a8a84',
                    '&:hover': {
                      backgroundColor: 'rgba(74, 138, 132, 0.2)',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(74, 138, 132, 0.1)',
                  },
                }}
              >
                <ListItemIcon sx={{ 
                  color: selectedTab === item.id ? '#4a8a84' : 'inherit'
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label} 
                  primaryTypographyProps={{
                    fontWeight: selectedTab === item.id ? 'medium' : 'regular',
                    color: selectedTab === item.id ? '#4a8a84' : 'inherit'
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider />
        <Box sx={{ mt: 'auto', p: 2 }}>
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Sair" />
            </ListItemButton>
          </ListItem>
        </Box>
      </Drawer>
      
      <Main open={drawerOpen && !isMobile}>
        <DrawerHeader />
        {renderContent()}
      </Main>
    </Box>
  );
}

export default Dashboard; 